import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import { Avatar, Box, CircularProgress, LinearProgress, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { times } from 'es-toolkit/compat';
import React from 'react';

import { AgentStreamProgress } from 'interfaces/agentSession';
import { MessageAvatar } from './MessageAvatar';
import { AVATAR_SIZE, fadeInKeyframes, ICON_SIZE } from './style';

export const LoadingSkeleton: React.FC = () => {
  const theme = useTheme();

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <Typography variant="body2" color="text.secondary">
        Loading conversation...
      </Typography>
      {times(3, (index) => {
        const isUser = index === 1;
        return (
          <Stack key={index} direction="row" spacing={1.5} alignItems="flex-start">
            <MessageAvatar
              role={isUser ? 'user' : 'assistant'}
              bgcolor={isUser ? theme.palette.grey[400] : theme.palette.primary.main}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton variant="text" width={isUser ? '18%' : '22%'} height={16} sx={{ mb: 0.75 }} />
              <Stack spacing={0.75}>
                <Skeleton variant="rounded" width={isUser ? '72%' : '88%'} height={14} />
                <Skeleton variant="rounded" width={isUser ? '56%' : '76%'} height={14} />
                {index === 2 ? <Skeleton variant="rounded" width="48%" height={14} /> : null}
              </Stack>
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
};

export const LoadingIndicator: React.FC = () => {
  const theme = useTheme();

  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Avatar sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE, bgcolor: theme.palette.primary.main }}>
        <FaceRetouchingNaturalIcon sx={{ fontSize: ICON_SIZE }} />
      </Avatar>
      <CircularProgress size={ICON_SIZE} />
    </Stack>
  );
};

export const AgentStatusIndicator: React.FC<{ text: string }> = ({ text }) => (
  <Stack
    direction="row"
    spacing={1.5}
    alignItems="center"
    sx={{
      pl: `${AVATAR_SIZE + 12}px`,
      animation: 'fadeInAction 0.35s ease-out',
      ...fadeInKeyframes,
    }}
  >
    <CircularProgress size={14} sx={{ color: 'text.secondary' }} />
    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
      {text}
    </Typography>
  </Stack>
);

export const AgentProgressIndicator: React.FC<{ progressEvent: AgentStreamProgress }> = ({ progressEvent }) => (
  <Stack
    spacing={0.75}
    sx={{
      pl: `${AVATAR_SIZE + 12}px`,
      animation: 'fadeInAction 0.35s ease-out',
      ...fadeInKeyframes,
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        {progressEvent.text}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {`${Math.round(progressEvent.progress)}%`}
      </Typography>
    </Stack>
    <LinearProgress
      variant="determinate"
      value={progressEvent.progress}
      aria-label={progressEvent.text}
      sx={{ height: 6, borderRadius: 999 }}
    />
  </Stack>
);
